import { useEffect, useRef } from 'react';
import {
    ONBOARDING_EXTERNAL_CHECKOUT_STEPS,
    ONBOARDING_EXTERNAL_CHECKOUT_SUBTASKS,
    ONBOARDING_KIT_STEPS,
    ONBOARDING_KIT_SUBTASKS,
    ONBOARDING_LITE_STEPS,
    ONBOARDING_LITE_SUBTASKS,
} from '../../api/constants';
import { getOnboardingVariant, type OnboardingVariant } from '../../api/onboardingVariant';
import { useIsOnboardingCompleted } from './useIsOnboardingCompleted';
import { useOnboardingStage } from './useOnboardingStage';

interface UseOnboardingPaymentArg {
    isCompleted: boolean;
}

const PAYMENT_ONBOARDING_CONFIG = {
    kit: {
        stageSlug: ONBOARDING_KIT_STEPS.DELIVERY_PAYMENT,
        subtaskSlug: ONBOARDING_KIT_SUBTASKS.SETUP_PAYMENT,
    },
    external: {
        stageSlug: ONBOARDING_EXTERNAL_CHECKOUT_STEPS.CONFIGURE_DELIVERY_AND_PAYMENT,
        subtaskSlug: ONBOARDING_EXTERNAL_CHECKOUT_SUBTASKS.CONFIGURE_PAYMENT,
    },
    lite: {
        stageSlug: ONBOARDING_LITE_STEPS.CONNECT_DELIVERY_AND_PAYMENT,
        subtaskSlug: ONBOARDING_LITE_SUBTASKS.CONNECT_PAYMENT,
    },
} satisfies Record<OnboardingVariant, { stageSlug: string; subtaskSlug: string }>;

export const useOnboardingPayment = ({ isCompleted }: UseOnboardingPaymentArg) => {
    const onboardingVariant = getOnboardingVariant();
    const { stageSlug, subtaskSlug } = PAYMENT_ONBOARDING_CONFIG[onboardingVariant];
    const { completeSubtask, resetSubtask } = useOnboardingStage(stageSlug);
    const isOnboardingCompleted = useIsOnboardingCompleted();

    const hasChangedSubtask = useRef<boolean>(false);

    // Костыль, чтобы избежать бесконечного спама authorizePlugin.
    // На каждое изменение этих зависимостей максимум один запрос.
    useEffect(() => {
        hasChangedSubtask.current = false;
    }, [isCompleted]);

    useEffect(() => {
        if (hasChangedSubtask.current) {
            return;
        }

        hasChangedSubtask.current = true;

        if (isOnboardingCompleted) {
            return;
        }

        if (isCompleted) {
            completeSubtask(subtaskSlug);
        } else {
            resetSubtask(subtaskSlug);
        }
    }, [completeSubtask, isCompleted, isOnboardingCompleted, resetSubtask, subtaskSlug]);
};
